import { useCallback, useState } from 'react';
import { CollectionPreferencesProps } from '@amzn/awsui-components-console';

import {
  loadTablePreferences,
  normaliseTablePreferences,
  sanitiseContentDisplay,
  saveTablePreferences,
  type TablePreferencesConfig,
  type TablePreferencesValue,
} from './tablePreferences';

/**
 * Holds one table's preferences: read on mount, normalised and persisted on each
 * confirm. A table whose columns vary by view (search's two modes) must hold one
 * value per view — it does not remount on a switch.
 */

interface UseTablePreferencesOptions {
  /**
   * Called when a confirm changed the page size. EVERY paginated table needs it:
   * Cloudscape only clamps an out-of-range page index, it never returns to page 1.
   */
  readonly onPageSizeChange?: (pageSize: number) => void;
}

interface UseTablePreferencesResult {
  readonly preferences: TablePreferencesValue;
  /** Pass to `TablePreferences`; takes the raw Cloudscape payload. */
  readonly onConfirm: (
    preferences: CollectionPreferencesProps.Preferences,
  ) => void;
  /** Pass to the Table's `columnDisplay`. Mutable-typed because that prop is. */
  readonly columnDisplay: CollectionPreferencesProps.ContentDisplayColumn[];
}

export function useTablePreferences(
  config: TablePreferencesConfig,
  options: UseTablePreferencesOptions = {},
): UseTablePreferencesResult {
  const { onPageSizeChange } = options;

  // Lazy: storage is read once, on mount.
  const [preferences, setPreferences] = useState<TablePreferencesValue>(() =>
    loadTablePreferences(config),
  );

  const onConfirm = useCallback(
    (confirmed: CollectionPreferencesProps.Preferences) => {
      const next = normaliseTablePreferences(confirmed, config);

      setPreferences(next);
      saveTablePreferences(config, next);

      // Outside the state updater: an updater must stay pure, and React may
      // invoke it more than once.
      if (
        next.pageSize !== undefined &&
        next.pageSize !== preferences.pageSize
      ) {
        onPageSizeChange?.(next.pageSize);
      }
    },
    [config, onPageSizeChange, preferences.pageSize],
  );

  // Reconciled every render, so a table whose columns change at runtime never
  // hands Cloudscape an id its `columnDefinitions` no longer contain.
  const contentDisplay = sanitiseContentDisplay(
    preferences.contentDisplay,
    config,
  );

  return {
    preferences: { ...preferences, contentDisplay },
    onConfirm,
    columnDisplay: contentDisplay,
  };
}
